import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@opendoor/bricks/core';
import { motion } from 'framer-motion';

import * as menuAnimations from './menuAnimations';
import { trapFocus } from '../trapFocus';
import type { MenuConfig, NavigationProps } from '../types';
import { MenuBackdrop } from './components/MenuBackdrop';
import { MenuContent } from './components/MenuContent';

interface MenuPanelProps extends NavigationProps, MenuConfig {
  isVisible?: boolean;
  setIsVisible: (isVisible: boolean) => void;
  exitComplete: () => void;
}

const Menu = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  showLanguageToggle,
  megaNavData,
}: MenuPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animate = isVisible ? 'visible' : 'hidden';

  const onComplete = (definition: 'visible' | 'hidden') => {
    if (definition === 'visible') {
      containerRef.current?.focus();
    } else {
      exitComplete();
    }
  };

  return (
    <Box
      as={motion.div}
      variants={menuAnimations.menuVariants}
      animate={animate}
      onKeyDown={(e) => trapFocus(e, containerRef)}
      bottom={0}
      display="grid"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
    >
      <MenuContent
        isVisible={!!isVisible}
        containerRef={containerRef}
        onAnimationComplete={onComplete}
        slug={slug}
        menuPanelNavLinks={menuPanelNavLinks}
        mainNavLinks={mainNavLinks}
        ctaLink={ctaLink}
        ctaTitle={ctaTitle}
        displayLanguageToggle={showLanguageToggle}
        megaNavData={megaNavData}
      />
      <MenuBackdrop isVisible={!!isVisible} onClick={() => setIsVisible(false)} />
    </Box>
  );
};

const MenuPanel = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  showLanguageToggle,
  megaNavData,
}: MenuPanelProps) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    document.body.classList.toggle('has-sheet', isVisible);
  }, [isVisible]);

  const escFunction = useCallback((event: { key: string }) => {
    if (event.key === 'Escape') {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  return mounted ? (
    <Menu
      slug={slug}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      ctaLink={ctaLink}
      ctaTitle={ctaTitle}
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      exitComplete={exitComplete}
      showLanguageToggle={showLanguageToggle}
      megaNavData={megaNavData}
    />
  ) : null;
};

export default MenuPanel;
